import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTaskById, deleteTask } from '../api/tasks';
import { getProjectById } from '../api/projects';
import { getMilestoneById } from '../api/milestones';
import { getProgrammeById } from '../api/programmes';
import { getAllUsers } from '../api/users';
import { Task } from '../types/task';
import { Project } from '../types/project';
import { Milestone } from '../types/milestone';
import { User } from '../types/user';
import TaskForm from '../components/TaskForm';
import DependencyChainView from '../components/DependencyChainView';
import Button from '../components/Button';
import Badge from '../components/Badge';
import Card from '../components/Card';
import EntityDetailLayout from '../components/detail/EntityDetailLayout';
import EvidencePanel from '../components/tasks/EvidencePanel';
import { evidencePanelEnabled } from '../config/features';
import { usePermissions } from '../hooks/usePermissions';

const formatDate = (dateString?: string | null): string =>
  dateString ? new Date(dateString).toLocaleDateString() : 'No due date';

const TaskDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canEditProject } = usePermissions();
  const [task, setTask] = useState<Task | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [milestone, setMilestone] = useState<Milestone | null>(null);
  const [programmeName, setProgrammeName] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [taskData, usersData] = await Promise.all([getTaskById(id), getAllUsers()]);
      if (!taskData) {
        setError('Task not found');
        return;
      }
      setTask(taskData);
      setUsers(usersData);

      const projectData = await getProjectById(taskData.project_id);
      setProject(projectData);

      if (taskData.milestone_id) {
        try {
          setMilestone(await getMilestoneById(taskData.milestone_id));
        } catch {
          setMilestone(null);
        }
      } else {
        setMilestone(null);
      }

      if (projectData.programme_id) {
        try {
          const programme = await getProgrammeById(projectData.programme_id);
          setProgrammeName(programme.name);
        } catch {
          setProgrammeName(null);
        }
      } else {
        setProgrammeName(null);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load task');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!task) return;
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    try {
      await deleteTask(task.id);
      navigate(task.milestone_id ? `/milestones/${task.milestone_id}` : `/projects/${task.project_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete task');
    }
  };

  const getUserName = (userId?: string | null): string => {
    if (!userId) return 'Unassigned';
    const user = users.find((u) => u.id === userId);
    return user ? user.full_name || user.username : 'Unknown';
  };

  const breadcrumb = [
    { label: 'Programmes', path: '/programmes' },
    ...(project?.programme_id
      ? [{ label: programmeName ?? 'Programme', path: `/programmes/${project.programme_id}` }]
      : []),
    ...(project ? [{ label: project.name, path: `/projects/${project.id}` }] : []),
    ...(milestone ? [{ label: milestone.name, path: `/milestones/${milestone.id}` }] : []),
    { label: task?.title ?? 'Task' },
  ];

  return (
    <>
      <EntityDetailLayout
        breadcrumb={breadcrumb}
        loading={loading}
        error={error}
        title={task?.title ?? ''}
        headerBadges={
          task ? (
            <>
              <Badge priority={task.priority as any} size="md">
                {task.priority}
              </Badge>
              <Badge status={task.status as any} size="md">
                {task.status.replace('_', ' ')}
              </Badge>
            </>
          ) : undefined
        }
        actions={
          task ? (
            <>
              <Button variant="primary" size="sm" onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button variant="danger" size="sm" onClick={handleDelete}>
                Delete
              </Button>
            </>
          ) : undefined
        }
      >
        {task && (
          <Card variant="elevated">
            <Card.Header title="Task Information" />
            <Card.Body>
              {task.description && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Description</p>
                  <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{task.description}</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Project</p>
                  <p className="text-gray-900 dark:text-white mt-1">{project?.name ?? '—'}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Milestone</p>
                  <p className="text-gray-900 dark:text-white mt-1">{milestone?.name ?? 'No Milestone'}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Assigned To</p>
                  <p className="text-gray-900 dark:text-white mt-1">{getUserName(task.assigned_to)}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Due Date</p>
                  <p className="text-gray-900 dark:text-white mt-1">{formatDate(task.due_date)}</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        )}

        {task && evidencePanelEnabled && (
          <EvidencePanel
            taskId={task.id}
            canEdit={project ? canEditProject(project.id, project.programme_id ?? undefined) : false}
          />
        )}

        {task && (
          <Card variant="elevated">
            <Card.Body>
              <DependencyChainView taskId={task.id} taskTitle={task.title} />
            </Card.Body>
          </Card>
        )}
      </EntityDetailLayout>

      {editing && task && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="w-full max-w-2xl my-8">
            <TaskForm
              task={task}
              onSuccess={() => {
                setEditing(false);
                load();
              }}
              onCancel={() => setEditing(false)}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default TaskDetailPage;
